import React, { useEffect, useState } from 'react';

import { Alert, Badge } from '@vivaahealth/design-system';

import * as api from '../../api';
import * as PhoneNumber from '../../utils/phoneNumber';

import { RescheduleView } from './RescheduleView';
import { Button } from '../../components/Button';
import { EhrTimeSlot } from '../../api';
import { DoctorAppointmentProfileView } from './DoctorAppointmentProfileView';
import { ModifyAppointmentModal } from './ModifyAppointmentModal';
import {
  isAppointmentCancellableNow,
  getCancelTimeWindow,
} from '../../utils/confirmCancel';

import styles from './index.module.css';
import { Trans, useTranslation } from 'react-i18next';
import { LinkText } from '../../components/LinkText';

interface AppointmentActionViewProps {
  appointment: api.NotificationIntent.Appointment;
  rescheduleIdentifier: EhrTimeSlot.RescheduleIdentifier;
  timezone: string;
  className?: string;
  pending?: boolean;
  cancelOnly?: boolean;
  setRescheduling: (val: boolean) => void;
  onRescheduled?: () => void;
  onCancelAppointment?: () => void;
  practicePhoneNumber?: string;
}

export const AppointmentActionView: React.FC<AppointmentActionViewProps> = ({
  appointment,
  rescheduleIdentifier,
  timezone,
  className,
  pending,
  cancelOnly,
  setRescheduling,
  onRescheduled,
  onCancelAppointment,
  practicePhoneNumber,
}) => {
  const { t } = useTranslation();
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] =
    useState(false);
  const [instructions, setInstructions] = useState<string | JSX.Element>('');
  const [requiresManualReschedule, setRequiresManualReschedule] =
    useState(false);

  const cancelConfirmModal = (
    <ModifyAppointmentModal
      title={t('Cancel Appointment')}
      instructions={t(
        'Are you sure you want to cancel this appointment? This action cannot be undone.',
      )}
      isOpen={cancelConfirmationModalOpen}
      setModalOpen={setCancelConfirmationModalOpen}
      onModifyAppointment={onCancelAppointment}
      pending={pending}
    />
  );
  const cancelButton = (
    <Button
      type="button"
      onClick={() => setCancelConfirmationModalOpen(true)}
      children={t('Cancel appointment')}
      invertColors={true}
      disabled={pending}
      className={className}
    />
  );
  const isCancellableNow = isAppointmentCancellableNow(appointment);

  useEffect(() => {
    const newInstructions =
      cancelOnly || requiresManualReschedule ? (
        practicePhoneNumber ? (
          <Trans
            i18nKey="This type of appointment does not allow online rescheduling. Give us a call at <link /> to reschedule, or click the button above to cancel your appointment."
            defaults="This type of appointment does not allow online rescheduling. Give us a call at <link /> to reschedule, or click the button above to cancel your appointment."
            components={{
              link: (
                <LinkText
                  className={styles.Link}
                  to={`tel:${practicePhoneNumber}`}
                  title={PhoneNumber.prettify(practicePhoneNumber)}
                />
              ),
            }}
          />
        ) : (
          t(
            'This type of appointment does not allow online rescheduling. Click the button above to cancel your appointment.',
          )
        )
      ) : (
        t(
          'Select a new time for your appointment below, or cancel if you can no longer attend.',
        )
      );
    setInstructions(newInstructions);
  }, [cancelOnly, practicePhoneNumber, t, requiresManualReschedule]);

  return (
    <>
      <DoctorAppointmentProfileView
        key={appointment.encounter.id}
        id={appointment.encounter.id}
        startTime={appointment.encounter.startTime}
        arrivalTime={appointment.encounter.arrivalTime || undefined}
        status={appointment.encounter.status || undefined}
        provider={appointment.provider}
        practice={appointment.practice}
        virtualVisit={appointment.virtualVisit}
        hideEncounterStartTimeByVisitKind={
          appointment.preferences.hideEncounterStartTimeByVisitKind
        }
        kindOfVisit={appointment.encounter.kindOfVisit || undefined}
        badge={
          !isCancellableNow ? (
            <Badge
              children={t('Within {{time}}', {
                time: getCancelTimeWindow(appointment, t),
              })}
              className={styles.DoctorProfileBadge}
              variant="danger"
            />
          ) : undefined
        }
        actionInfoContext={
          <>
            {!isCancellableNow && (
              <Alert
                className={styles.MessageTopMargin}
                variant="danger"
                title={t('Cancellation window has passed')}
                cta={{
                  label: t('Cancel appointment'),
                  onClick: () => setCancelConfirmationModalOpen(true),
                  disabled: pending,
                }}
              >
                {t(
                  'Cancelling appointments within {{time}} will require a call to the clinic listed above.',
                  {
                    time: getCancelTimeWindow(appointment, t),
                  },
                )}
              </Alert>
            )}
            {isCancellableNow && cancelButton}
          </>
        }
      />
      {instructions}
      {cancelConfirmModal}
      {!cancelOnly && isCancellableNow && !requiresManualReschedule && (
        <RescheduleView
          rescheduleIdentifier={rescheduleIdentifier}
          timezone={timezone}
          setIsRescheduling={setRescheduling}
          onRescheduled={onRescheduled}
          onRequiresManual={() => setRequiresManualReschedule(true)}
        />
      )}
    </>
  );
};
